/**
 * Seitliche Navigation ab Tablet-Breite.
 *
 * Liest ausschliesslich aus der Navigations-Registry — wer einen Bereich
 * ergänzt, ergänzt ihn dort und er erscheint hier, in der mobilen Leiste und
 * in der Palette gleichzeitig.
 *
 * **Zwei Ebenen, bewusst:** Die fünf Bereiche tragen die Gliederung, ihre Ziele
 * stehen darunter. Ein Bereich mit nur einem Ziel wird nicht aufgeklappt — das
 * wäre eine Überschrift über sich selbst. Bereiche mit mehreren Zielen zeigen
 * sie alle, denn sonst hätten „Nutzung", „API-Historie" und „Designs" gar
 * keinen sichtbaren Einstieg mehr; sie waren nach dem Verdichten von acht
 * Punkten auf fünf Bereiche nur noch über ⌘K erreichbar.
 *
 * Die Leiste reicht über die volle Fensterhöhe und bleibt beim Scrollen stehen.
 */
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { UserRole } from '@/types';
import { bereicheFuerRolle, bereichFuerPfad, type NavZiel } from '@/lib/navigation';
import { DocklyLogo } from '@/components/brand/DocklyLogo';

export interface AppSidebarProps {
  className?: string;
  /**
   * Rolle des angemeldeten Nutzers. Ziele mit `mindestrolle` erscheinen nur,
   * wenn sie reicht — ohne Angabe also nicht. Rein kosmetisch: das Backend
   * entscheidet.
   */
  rolle?: UserRole;
}

/** Exakte Übereinstimmung für die Übersicht, sonst auch Unterpfade. */
function istAktivesZiel(ziel: NavZiel, pfad: string): boolean {
  return ziel.href === '/admin'
    ? pfad === '/admin'
    : pfad === ziel.href || pfad.startsWith(`${ziel.href}/`);
}

export function AppSidebar({ className = '', rolle }: AppSidebarProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const aktiverBereich = bereichFuerPfad(pathname);

  return (
    <nav
      aria-label={t('nav.adminArea')}
      className={`sticky top-0 flex h-screen w-60 shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-surface-200 bg-surface-100 px-3 py-4 ${className}`.trim()}
    >
      <Link to="/admin" aria-label={t('nav.home')} className="mb-4 flex items-center gap-2 px-2">
        <DocklyLogo height={26} />
      </Link>

      {bereicheFuerRolle(rolle).map((bereich) => {
        const Icon = bereich.icon;
        const bereichAktiv = aktiverBereich?.id === bereich.id;
        // Ein einzelnes Ziel braucht keine zweite Ebene.
        const einzeln = bereich.ziele.length === 1;
        const ziel = bereich.ziele[0];

        if (einzeln && ziel) {
          const aktiv = istAktivesZiel(ziel, pathname);
          return (
            <Link
              key={bereich.id}
              to={ziel.href}
              aria-current={aktiv ? 'page' : undefined}
              className={[
                'flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors',
                aktiv
                  ? 'bg-surface-200 font-semibold text-surface-900'
                  : 'text-surface-600 hover:bg-surface-200/60 hover:text-surface-900',
              ].join(' ')}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t(bereich.labelKey)}</span>
            </Link>
          );
        }

        return (
          <div key={bereich.id} className="mb-1">
            <div
              className={[
                'flex items-center gap-3 rounded-xl px-3 py-2 text-sm',
                bereichAktiv ? 'font-semibold text-surface-900' : 'text-surface-600',
              ].join(' ')}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t(bereich.labelKey)}</span>
            </div>

            <div className="ml-4 flex flex-col gap-0.5 border-l border-surface-200 pl-3">
              {bereich.ziele.map((z) => {
                const aktiv = istAktivesZiel(z, pathname);
                return (
                  <Link
                    key={z.href}
                    to={z.href}
                    aria-current={aktiv ? 'page' : undefined}
                    className={[
                      'rounded-lg px-2.5 py-1.5 text-sm transition-colors',
                      aktiv
                        ? 'bg-surface-200 font-semibold text-surface-900'
                        : 'text-surface-500 hover:bg-surface-200/60 hover:text-surface-900',
                    ].join(' ')}
                  >
                    {t(z.labelKey)}
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}
    </nav>
  );
}

export default AppSidebar;
